import { clsx } from "clsx";
import React, { useEffect, useState } from "react";
import { LeadLink } from "web/react/components/lead-link";
import Select from "web/react/components/select/select";
import { Option } from "web/react/components/select/types";
import { SizeOption } from "web/react/components/size-picker/size-option";
import {
    ProductPurchaseTypes,
    useInStockProductPageContext,
} from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { Features } from "web/types/analytics";
import { Schemas } from "web/types/examples/schemas";
import { ProductScreenSize } from "web/types/serializers";
import {
    SelectableSchemaSizePickerOptionHeading,
    SizePickerOptionHeading,
    SizePickerOptionHeadingProps,
} from "./option-heading";
import { centred, optionText, row } from "./size-option.css";
import * as styles from "./size-picker.css";

export function compileSizeOptions(
    sizes: ProductScreenSize[],
    ignoreStockStatus = false,
    selectedTextPrefix?: string
): Option[] {
    return sizes.map((size) => {
        const inStock = size.in_stock === undefined ? true : size.in_stock;
        const selectedText = selectedTextPrefix
            ? `${selectedTextPrefix}${size.display_size}`
            : size.display_size;
        return {
            selectedText,
            text: [size.display_size],
            value: size,
            disabled: ignoreStockStatus ? false : !inStock,
        };
    });
}

function sizePickerFooter(): JSX.Element {
    const sizePickerDisclaimerLabel = gettext("product.buy_area.size_picker.supply_label");
    return (
        <div>
            <div className={styles.divider}></div>
            <div className={styles.disclaimer}>{sizePickerDisclaimerLabel}</div>
        </div>
    );
}

function expressCheckoutFooter(
    productId: string,
    leadUrl: string,
    afterOnClick: (expanded: boolean, shouldSendAnalytic: boolean) => void
): JSX.Element {
    return (
        <LeadLink
            href={leadUrl}
            productId={productId}
            openInNewTab={true}
            reason="click-to-see-more-sizes"
            afterOnClick={() => {
                afterOnClick(true, false);
            }}
            className={clsx(row, centred, optionText)}
        >
            {gettext("product.buy_area.see_more_sizes")}
        </LeadLink>
    );
}

export function compileLocalizedSizeOptions(
    sizes: ProductScreenSize[],
    schema: string,
    ignoreStockStatus = false,
    selectedTextPrefix?: string
): Option[] {
    const validSizes = sizes.filter((size) => {
        if (size.localized_sizes?.[schema]) {
            return size;
        }
    });
    return validSizes.map((size: ProductScreenSize) => {
        const localizedSizeText = size.localized_sizes?.[schema].display_size as string;
        const retailerSizeText = size.retailer_size?.display_size;
        const inStock = size.in_stock === undefined ? true : size.in_stock;
        const defaultSelectedText =
            localizedSizeText === retailerSizeText
                ? localizedSizeText
                : `${localizedSizeText} / ${retailerSizeText}`;
        const selectedText = selectedTextPrefix
            ? `${selectedTextPrefix}${defaultSelectedText}`
            : defaultSelectedText;

        return {
            selectedText,
            text: [localizedSizeText, retailerSizeText],
            value: size,
            disabled: ignoreStockStatus ? false : !inStock,
        } as Option;
    });
}

function createSelectableSchemaSizePickerOptionHeading(
    onSchemaSelect,
    schemas,
    selectedSchema
): (SizePickerOptionHeadingProps: SizePickerOptionHeadingProps) => JSX.Element {
    return function SelectableSchemaSizePickerOptionHeadingWrapper({
        onCloseButtonClick,
    }: SizePickerOptionHeadingProps): JSX.Element {
        return (
            <SelectableSchemaSizePickerOptionHeading
                onCloseButtonClick={onCloseButtonClick}
                onSchemaSelect={onSchemaSelect}
                schemas={schemas}
                selectedSchema={selectedSchema}
            />
        );
    };
}

export interface SizePickerProps {
    isExpanded: boolean;
    onSelect: (option: Option, isComparison: boolean) => void;
    schemas: Schemas;
    setExpanded: (expanded: boolean, shouldSendAnalytic?: boolean) => void;
    sizes: ProductScreenSize[];
    userDefaultSchema: string;
    isComparison?: boolean;
    defaultSelectText?: string;
    error?: boolean;
    productType: ProductPurchaseTypes;
    showDisabledOptions?: boolean;
    selectedOption?: ProductScreenSize;
    isExpressCheckout?: boolean;
    showAllSchemas?: boolean;
    selectedTextPrefix?: string;
}

export function SizePicker({
    isExpanded,
    onSelect,
    schemas,
    setExpanded,
    sizes,
    userDefaultSchema,
    isComparison,
    defaultSelectText = "Select a size",
    error = false,
    productType,
    showDisabledOptions = false,
    selectedOption,
    isExpressCheckout = false,
    showAllSchemas = false,
    selectedTextPrefix,
}: SizePickerProps): JSX.Element {
    // TODO: i18n
    const [selectedSchema, setSelectedSchema] = useState(userDefaultSchema);

    const {
        product,
        activeBuyOption: { buyOption: activeBuyOption },
    } = useInStockProductPageContext();

    const localizedSizes = sizes.filter((size) => {
        return size.localized_sizes;
    });

    let hasLocalizedSizes = showAllSchemas || (!!selectedSchema && localizedSizes.length > 0);
    const ANALYTICS_CATEGORY = isComparison ? "compare_prices_size_picker" : "size_picker";

    function getSizeOptions(): Option[] {
        if (hasLocalizedSizes) {
            const localizedSizeOptions = compileLocalizedSizeOptions(
                localizedSizes,
                selectedSchema,
                showAllSchemas,
                selectedTextPrefix
            );

            // Make sure at least one option is available
            const areOptionsAvailable = localizedSizeOptions.some((option: Option) => {
                return !option.disabled;
            });

            if (showAllSchemas || areOptionsAvailable) {
                return localizedSizeOptions;
            }
        }

        return compileSizeOptions(sizes, showAllSchemas, selectedTextPrefix);
    }

    const [sizeOptions, setSizeOptions] = useState<Option[]>(getSizeOptions());
    const [pickedOption, setPickedOption] = useState<Option | undefined>(undefined);

    useEffect(() => {
        if (!isComparison && !isExpressCheckout) {
            analytics.event(ANALYTICS_CATEGORY, "shown");
        }
    }, []);

    useEffect(() => {
        setSizeOptions(getSizeOptions());
    }, [selectedSchema]);

    useEffect(() => {
        if (selectedOption) {
            let selectedSizeOption: Option | undefined;
            if (hasLocalizedSizes) {
                selectedSizeOption = compileLocalizedSizeOptions(
                    [selectedOption],
                    selectedSchema,
                    showAllSchemas,
                    selectedTextPrefix
                )[0];
            } else {
                selectedSizeOption = compileSizeOptions(
                    [selectedOption],
                    showAllSchemas,
                    selectedTextPrefix
                )[0];
            }

            setPickedOption(selectedSizeOption);
        }
    }, [selectedOption, hasLocalizedSizes, selectedSchema, sizes, selectedTextPrefix]);

    function getFeatures(): Features {
        const features = {
            selectable_schema: hasLocalizedSizes,
        };
        return features;
    }

    function handleFirstExpand(): void {
        if (isExpressCheckout) {
            return;
        }

        const action = "first_expansion";
        const subtype = `checkout.${ANALYTICS_CATEGORY}.${action}`;

        // Trigger an analytic to send:
        // - Num Sizes
        // - Which schema is initially selected
        // - What features the size picker contains
        analytics.event(ANALYTICS_CATEGORY, action, "", false, null, subtype, {
            size_picker: {
                first_expansion: {
                    current_schema: selectedSchema,
                    features: getFeatures(),
                    sizes_available: localizedSizes.length || sizes.length,
                },
            },
        });
    }

    function handleSchemaSelect(event): void {
        const toSchema = event.target.value;
        const fromSchema = selectedSchema;

        setSelectedSchema(toSchema);

        const category = "size_picker";
        const action = "schema_change";
        const subType = `${productType}.${category}.${action}`;

        // Track which schema the user was on, and what they switched to
        analytics.event(category, action, undefined, false, null, subType, {
            size_picker: {
                schema_change: {
                    from_schema: fromSchema,
                    to_schema: toSchema,
                },
            },
        });
    }

    function onTouchOverlay(): void {
        setExpanded(false);
    }

    return (
        <>
            {isExpanded && <div className={styles.overlay} onTouchStart={onTouchOverlay}></div>}
            <Select
                dataTestId="size-picker-select"
                defaultSelectText={defaultSelectText}
                error={error}
                isExpanded={isExpanded}
                onFirstExpand={handleFirstExpand}
                onSelect={(option) => onSelect(option, !!isComparison)}
                onDisabledSelect={(option) => onSelect(option, !!isComparison)}
                OptionComponent={SizeOption}
                OptionHeadingComponent={
                    hasLocalizedSizes
                        ? createSelectableSchemaSizePickerOptionHeading(
                              handleSchemaSelect,
                              schemas,
                              selectedSchema
                          )
                        : SizePickerOptionHeading
                }
                OptionFooterComponent={
                    isExpressCheckout
                        ? expressCheckoutFooter(
                              product.product_id,
                              activeBuyOption.affiliate_url,
                              setExpanded
                          )
                        : sizePickerFooter()
                }
                options={sizeOptions}
                setExpanded={setExpanded}
                showOnlyOptionByDefault={true}
                showDisabledOptions={showDisabledOptions}
                pickedOption={pickedOption}
            />
        </>
    );
}
